/**
 * RecoveryDialog — prompts the user to restore an interrupted session
 * found in IndexedDB.
 *
 * Mirrors handleRecoverGame / handleDiscardRecovery from EstadisticasNew.js.
 */

import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box } from '@mui/material';
import RestoreIcon from '@mui/icons-material/Restore';
import { useStatsTheme } from '../../theme/statsTheme';

export default function RecoveryDialog({ open, recoveryData, onRestore, onDiscard, t }) {
  const theme = useStatsTheme();
  const tt = (key, fallback) => (typeof t === 'function' ? t(`estadisticasNew.${key}`, { defaultValue: fallback }) : fallback);

  const state = recoveryData?.state;
  const actionCount = state?.actionsLog?.length || 0;
  const period = state?.currentPeriod || 1;

  return (
    <Dialog
      open={open}
      onClose={onDiscard}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: theme.surfaces.elevated,
          color: theme.text.primary,
          border: `1px solid ${theme.accentBorder}`,
          borderRadius: theme.radius.xl,
        },
      }}
    >
      <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
        <RestoreIcon sx={{ color: theme.accent }} />
        {tt('recoveryTitle', 'Recover unsaved session?')}
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: theme.text.secondary, mb: 1 }}>
          {tt('recoveryMessage', 'A previous game was interrupted before save. You can restore it or discard it.')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 1.5 }}>
          <Box>
            <Typography sx={{ fontSize: 11, color: theme.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {tt('actions', 'Actions')}
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: theme.text.primary }}>
              {actionCount}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontSize: 11, color: theme.text.muted, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              {tt('period', 'Period')}
            </Typography>
            <Typography sx={{ fontSize: 18, fontWeight: 700, color: theme.text.primary }}>
              P{period}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onDiscard} sx={{ color: theme.actionColors.missed }}>
          {tt('recoveryDiscard', 'Discard')}
        </Button>
        <Button
          onClick={onRestore}
          variant="contained"
          sx={{
            backgroundColor: theme.accent,
            '&:hover': { backgroundColor: theme.accentStrong },
          }}
        >
          {tt('recoveryRestore', 'Restore')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
